<template>
    <div class="enquiry-form">
        <AuthFormInModal
            ref="authForm"
            :content
            :is-open="isOpen"
            :registration-disabled="fetching"
            :registration-errors="errors"
            registration-hide-inputs-if-logged
            @close="close"
            @login="
                (_, type) => {
                    if (type === 'manual') submit()
                }
            "
            @register="submit"
        >
            <template #registration-contact>
                <FormRow>
                    <ThSelectVue
                        v-model="purpose"
                        :error="errors.last('input.purpose')"
                        :legend="'Enquiry purpose'"
                        :options="purposeOptions"
                        :placeholder="'Enquiry purpose'"
                        class="purpose"
                    />
                </FormRow>

                <FormRow>
                    <TextArea
                        id="form-message"
                        v-model="instructions"
                        :error="errors.last('input.instructions')"
                        :placeholder="'Please enter any questions, requirements or ideas you may have'"
                        class="instructions"
                        required
                    />
                </FormRow>
            </template>
        </AuthFormInModal>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, useTemplateRef } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

import type {
    EnquiryContactUsEnquiryPurpose,
    EnquiryContactUsInput,
} from '@/graphql/api'
import { getCommissionJunctionInput } from '@/helpers/commission-junction'
import {
    createResponseAndPath,
    useLastResponseErrorAtPath,
} from '@/helpers/errors'

import { useContactUs } from '@/modules/contact-us'
import { useCustomerRegistrationInputStore } from '@/composables/useCustomerRegistrationInput'

import { useRoutes } from '@/router/routes'
import { useIsAdmin } from '@/components/app/isAdmin'
import type { SelectOption } from '@/components/ui/inputs/th-select.ts'
import { useAuth } from '@/plugins/auth.ts'
import { useCreateEnquiryContactUsExistingCustomerMutation } from '@/components/enquiry/api/mutations/createEnquiryContactUsExistingCustomer.generated.ts'
import { useCreateEnquiryContactUsNewCustomerMutation } from '@/components/enquiry/api/mutations/createEnquiryContactUsNewCustomer.generated.ts'

import TextArea from '@/components/ui/inputs/TextArea.vue'
import ThSelectVue from '../ui/inputs/ThSelect.vue'
import FormRow from '@/components/ui/form/FormRow.vue'
import AuthFormInModal from '../user/auth/AuthFormInModal.vue'
import { useInit } from '@/plugins/init/use-init.ts'
import { useCurrentUrl } from '@/plugins/current-url.ts'

const { t } = useI18n()
const { isOpen, close } = useContactUs()
const routes = useRoutes()

const { enquiryFormContent } = useInit()
const content = computed(() => enquiryFormContent.value['contact-us'])

const authForm = useTemplateRef('authForm')

const purposeOptions = computed<SelectOption<EnquiryContactUsEnquiryPurpose>[]>(
    () => [
        {
            label: t('enquiry.form.contact_us.aftercare'),
            value: 'AFTERCARE',
        },
        {
            label: t('enquiry.form.contact_us.product'),
            value: 'PRODUCT',
        },
        {
            label: t('enquiry.form.contact_us.returning'),
            value: 'RETURNING',
        },
    ]
)

const purpose = ref<EnquiryContactUsEnquiryPurpose>('AFTERCARE')
const instructions = ref('')

const commissionJunction = getCommissionJunctionInput()
const currentUrl = useCurrentUrl()

const { isLogged } = useAuth()

const existingCustomerResponse =
    useCreateEnquiryContactUsExistingCustomerMutation()
const {
    executeMutation: contactUsExistingCustomerMutation,
    fetching: contactUsExistingCustomerFetching,
} = existingCustomerResponse

const newCustomerResponse = useCreateEnquiryContactUsNewCustomerMutation()
const {
    executeMutation: contactUsNewCustomerMutation,
    fetching: contactUsNewCustomerFetching,
} = newCustomerResponse

const fetching = computed(
    () =>
        contactUsExistingCustomerFetching.value ||
        contactUsNewCustomerFetching.value
)

const errors = useLastResponseErrorAtPath([
    createResponseAndPath(
        existingCustomerResponse,
        'createEnquiryContactUsExistingCustomer'
    ),
    createResponseAndPath(
        newCustomerResponse,
        'createEnquiryContactUsNewCustomer'
    ),
])

const { customerRegistrationInput } = storeToRefs(
    useCustomerRegistrationInputStore()
)

const isAdmin = useIsAdmin()

const doSubmit = (successCallback?: () => void) => {
    const uri = currentUrl.value.href

    const input: EnquiryContactUsInput = {
        uri,
        purpose: purpose.value,
        instructions: instructions.value,
        commissionJunction,
    }

    if (isLogged.value) {
        return contactUsExistingCustomerMutation({ input })
    }

    const customer = customerRegistrationInput.value

    if (!customer) {
        return
    }

    return contactUsNewCustomerMutation({
        input,
        customer,
        isConsultant: isAdmin.value,
    }).then((res) => {
        if (res.data?.createEnquiryContactUsNewCustomer.customer) {
            successCallback?.()
        }

        return res
    })
}

const submit = (successCallback?: () => void) => {
    doSubmit(successCallback)?.then((response) => {
        const { data, error } = response

        if (error || !data) {
            return
        }

        authForm.value?.handleRegistrationResponse?.(
            'createEnquiryContactUsNewCustomer' in data
                ? data.createEnquiryContactUsNewCustomer.customer
                : undefined,
            routes.designStudio()
        )
    })
}
</script>
